import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

const MODES = [
  {
    key: 'thesis',
    label: { en: 'Thesis / Book', ar: 'رسالة / كتاب', fa: 'پایان‌نامه / کتاب' },
    desc:  { en: 'Seamless merge — one continuous document', ar: 'دمج سلس — مستند واحد متصل', fa: 'ادغام یکپارچه — یک سند پیوسته' },
  },
  {
    key: 'articles',
    label: { en: 'Articles / Conference', ar: 'مقالات / مؤتمر', fa: 'مقالات / کنفرانس' },
    desc:  { en: 'Page break between each document', ar: 'فاصل صفحة بين كل مستند', fa: 'شکست صفحه بین هر سند' },
  },
  {
    key: 'mixed',
    label: { en: 'Mixed Styles', ar: 'أنماط مختلطة', fa: 'سبک‌های مختلط' },
    desc:  { en: 'First file sets styles, direct formatting stripped from others', ar: 'الملف الأول يضبط الأنماط، يُحذف التنسيق المباشر من الباقي', fa: 'فایل اول سبک را تعیین می‌کند، قالب‌بندی مستقیم از بقیه حذف می‌شود' },
  },
];

export default function MergeWordCard({ lang }) {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('thesis');

  const onDrop = useCallback((accepted) => {
    const docx = accepted.filter(f => f.name.endsWith('.docx'));
    if (docx.length < accepted.length) {
      toast.error(lang === 'ar' ? 'يُسمح فقط بملفات .docx' : lang === 'fa' ? 'فقط فایل‌های .docx مجاز است' : 'Only .docx files are allowed');
    }
    setFiles(prev => {
      const combined = [...prev, ...docx];
      if (combined.length > 20) {
        toast.error(lang === 'ar' ? 'الحد الأقصى 20 ملف' : lang === 'fa' ? 'حداکثر ۲۰ فایل' : 'Maximum 20 files');
        return combined.slice(0, 20);
      }
      return combined;
    });
  }, [lang]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
    multiple: true,
  });

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const moveUp = (idx) => {
    if (idx === 0) return;
    setFiles(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (idx) => {
    setFiles(prev => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const handleMerge = async () => {
    if (files.length < 2) {
      toast.error(lang === 'ar' ? 'يلزم ملفان على الأقل' : lang === 'fa' ? 'حداقل ۲ فایل لازم است' : 'At least 2 files required');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('mode', mode);
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await axios.post(`${API}/api/convert/merge-word`, formData, {
        responseType: 'blob',
        headers,
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'merged.docx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(
        lang === 'ar' ? 'تم الدمج بنجاح! 🎉' : lang === 'fa' ? 'ادغام موفقیت‌آمیز بود! 🎉' : 'Merged successfully! 🎉',
        { style: { background: '#13131f', color: '#00d4aa', border: '1px solid rgba(0,212,170,0.3)' } }
      );
    } catch (err) {
      let msg = lang === 'ar' ? 'فشل الدمج' : lang === 'fa' ? 'ادغام ناموفق بود' : 'Merge failed';
      if (err.response?.data instanceof Blob) {
        try { const text = await err.response.data.text(); msg = JSON.parse(text).detail || msg; } catch { }
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const label = {
    drop: lang === 'ar' ? 'اسحب ملفات .docx هنا أو انقر للاختيار' : lang === 'fa' ? 'فایل‌های .docx را اینجا بکشید یا کلیک کنید' : 'Drag .docx files here or click to select',
    order: lang === 'ar' ? 'ترتيب الدمج' : lang === 'fa' ? 'ترتیب ادغام' : 'Merge order',
    merge: lang === 'ar' ? '⚡ دمج الآن' : lang === 'fa' ? '⚡ ادغام کن' : '⚡ Merge Now',
    merging: lang === 'ar' ? 'جاري الدمج...' : lang === 'fa' ? 'در حال ادغام...' : 'Merging...',
    clear: lang === 'ar' ? 'مسح الكل' : lang === 'fa' ? 'پاک کردن' : 'Clear All',
    modeLabel: lang === 'ar' ? 'نمط الدمج' : lang === 'fa' ? 'حالت ادغام' : 'Merge Mode',
  };

  return (
    <div>
      {/* Mode picker */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
        }}>
          {label.modeLabel}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {MODES.map(m => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              style={{
                background: mode === m.key ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${mode === m.key ? '#6366f1' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '8px',
                padding: '10px 14px',
                textAlign: lang === 'ar' || lang === 'fa' ? 'right' : 'left',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                fontSize: '13px', fontWeight: 600,
                color: mode === m.key ? '#a5b4fc' : 'rgba(255,255,255,0.75)',
              }}>
                {m.label[lang] || m.label.en}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>
                {m.desc[lang] || m.desc.en}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? '#6366f1' : 'rgba(255,255,255,0.15)'}`,
          borderRadius: '12px',
          padding: '32px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: isDragActive ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.02)',
          transition: 'all 0.2s',
          marginBottom: '16px',
        }}
      >
        <input {...getInputProps()} />
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>{label.drop}</div>
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '6px' }}>
          .docx — {lang === 'ar' ? 'حتى 20 ملف' : lang === 'fa' ? 'تا ۲۰ فایل' : 'up to 20 files'}
        </div>
      </div>

      {files.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
          }}>
            {label.order} ({files.length})
          </div>
          {files.map((f, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'rgba(255,255,255,0.04)', borderRadius: '8px',
              padding: '8px 12px', marginBottom: '6px',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', minWidth: '20px' }}>
                {i + 1}.
              </span>
              <span style={{ flex: 1, fontSize: '13px', color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.name}
              </span>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                {(f.size / 1024).toFixed(0)} KB
              </span>
              <button onClick={() => moveUp(i)} disabled={i === 0} style={arrowBtn}>▲</button>
              <button onClick={() => moveDown(i)} disabled={i === files.length - 1} style={arrowBtn}>▼</button>
              <button onClick={() => removeFile(i)} style={{ ...arrowBtn, color: '#e94560' }}>✕</button>
            </div>
          ))}
          <button
            onClick={() => setFiles([])}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
              fontSize: '12px', cursor: 'pointer', padding: '4px 0',
            }}
          >
            {label.clear}
          </button>
        </div>
      )}

      <button
        className="btn-primary"
        onClick={handleMerge}
        disabled={files.length < 2 || loading}
        style={{ opacity: files.length < 2 ? 0.5 : 1 }}
      >
        {loading ? <><span className="spinner" />{label.merging}</> : label.merge}
      </button>
    </div>
  );
}

const arrowBtn = {
  background: 'none',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '4px',
  color: 'rgba(255,255,255,0.5)',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '11px',
  lineHeight: 1.4,
};
